import { defineComponent as _defineComponent } from 'vue'
import {computed, onMounted, onUnmounted, PropType, ref, unref, watch} from "vue";
import {defaultFetch} from "@/scripts/Common/api";
import {Issuer} from "@/model/Issuer";
import {debounce, getMinDateRestriction, isEmpty} from "@/scripts/Common/HelperFunctions";
import {TopUpRotation} from "@/Enums";
import {useI18n} from "@/composables/useI18n";
import DateInput from "@/components/base/DateInput.vue";
import {useWebchargeSettings} from "@/composables/useWebchargeSettings";
import {useCardLimites} from "@/composables/useCardLimites";
import {useGratificationSettings} from "@/composables/useGratificationSettings";
import {IssuerCard} from "@/components/issuers/scripts/issuerCards";
import {
  hasValidAmount,
  hasValidRotationEnd,
  UpdateOrCreateConfiguredWebcharge
} from "@/components/issuers/scripts/configuredWebcharges";
import {useWebchargeRotations} from "@/components/issuers/composables/useWebchargeRotations";
import {getToken} from "@/scripts/Common";


export default /*#__PURE__*/_defineComponent({
  __name: 'AddIssuerWebchargesDialog',
  props: {
  selectedIssuer: {
    required: true,
    type: Issuer as PropType<Issuer>,
  }
},
  emits: [
  'onDialogCancel',
  'onDialogConfirm',
  'onError',
],
  setup(__props, { emit: emits }) {

const props = __props;

const comboSelection = ref<IssuerCard[]>([]);
const comboValue = ref<string | undefined>(undefined);
const issuerCards = ref<IssuerCard[]>([]);

const amount = ref<number>(0);
const rotation = ref<TopUpRotation>(TopUpRotation.None);
const nextChargingDate = ref<string>();
const rotationEnd = ref<string>();

const isValid = ref(true);
const webchargesToCreate = ref<UpdateOrCreateConfiguredWebcharge[]>([]);

const isDialogVisible = ref<boolean>(false);
const addWebchargesForm = ref();

const {
  t,
} = useI18n();



const {
  error: webchargeSettingsError,
  getEnabledDates
} = useWebchargeSettings();

const {
  error: cardLimiteError,
  cardLimite
} = useCardLimites();

const {
  error: gratificationError,
  gratificationSettings
} = useGratificationSettings();

const {
  rotations
} = useWebchargeRotations();



const getCurrentAllowedAmount = () => {
  switch (rotation.value) {
    case TopUpRotation.Monthly: {
      return cardLimite.value.maxMonthlyTopUpAmount;
    }
    case TopUpRotation.Gratification: {
      return gratificationSettings.value.maximumAmountPerCharge;
    }
    default: {
      return cardLimite.value.maxCardLimit > cardLimite.value.topUpLimitPerMonth
        ? cardLimite.value.maxCardLimit
        : cardLimite.value.topUpLimitPerMonth;
    }
  }
}

const loadIssuerCards = async (filterCardNumber: string | undefined) => {
  if (props.selectedIssuer.IsNew) return;

  const {
    error,
    response,
  } = await defaultFetch({
    apiName: 'getIssuerCardList',
    bearer: getToken(),
    options: {
      query: {
        issuerId: props.selectedIssuer.Id,
        cardNumber: filterCardNumber
      }
    }
  });

  if (error) {
    onHandleDialogError(error);
    return;
  }

  // type transformation needed for combo selection without type error
  issuerCards.value = response.map((card: IssuerCard) => ({
    ...card,
    cardNumber: card.cardNumber.toString()
  }));
};

const onHandleDialogError = (error: any) => {
  emits('onError', {error});
};

const onHandleDialogCancel = () => {
  emits('onDialogCancel');
}

const onHandleDialogConfirm = () => {
  webchargesToCreate.value = comboSelection.value.map((card: IssuerCard) => ({
    id: 0,
    cardId: card.cardId,
    amount: unref(amount),
    rotation: unref(rotation),
    nextChargingDate: unref(isTopRotationNoneSelected) ? undefined : unref(nextChargingDate),
    rotationEnd: unref(isTopRotationNoneSelected) ? undefined : unref(rotationEnd)
  }));

  emits('onDialogConfirm', webchargesToCreate);
}
const isTopRotationNoneSelected = computed(() =>
  rotation.value === TopUpRotation.None
);

const validateForm = () => {
  addWebchargesForm.value.validate();
}

onMounted(() => {
  isDialogVisible.value = true;
});

onUnmounted(() => {
  isDialogVisible.value = false;
})

watch(() => comboValue.value, debounce(() => {
  if (isEmpty(comboValue.value)) comboValue.value = undefined;
  loadIssuerCards(comboValue.value);
}));

watch(webchargeSettingsError, () => {
  onHandleDialogError(webchargeSettingsError)
});

watch(gratificationError, () => {
  onHandleDialogError(gratificationError)
});

watch(cardLimiteError, () => {
  onHandleDialogError(cardLimiteError)
});

return { __sfc: true,comboSelection, comboValue, issuerCards, amount, rotation, nextChargingDate, rotationEnd, isValid, webchargesToCreate, isDialogVisible, addWebchargesForm, t, props, webchargeSettingsError, getEnabledDates, cardLimiteError, cardLimite, gratificationError, gratificationSettings, rotations, emits, getCurrentAllowedAmount, loadIssuerCards, onHandleDialogError, onHandleDialogCancel, onHandleDialogConfirm, isTopRotationNoneSelected, validateForm, getMinDateRestriction, DateInput, hasValidAmount, hasValidRotationEnd }
}

})